import React, { useState, useRef } from "react";
import { Input, Select, Button, message } from "antd";
import IconPicker from "../../common/IconPicker";
import DynamicIcon from "../../common/DynamicIcon";

function CategoryForm({ onSuccess, onCancel }) {
    const [name, setName] = useState("");
    const [status, setStatus] = useState("active");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const fileInputRef = useRef(null);

    // Icon picker state
    const [selectedIcon, setSelectedIcon] = useState(null);
    const [iconPickerVisible, setIconPickerVisible] = useState(false);

    // Image handlers
    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                message.error("Please upload an image file");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                message.error("Image size should be less than 5MB");
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Submit handler - collect data and pass to parent (NO API call here)
    const handleSubmit = async () => {
        if (!imageFile) {
            message.error("Please upload a category image");
            return;
        }
        if (!name.trim()) {
            message.error("Please enter category name");
            return;
        }
        if (!selectedIcon) {
            message.error("Please select a category icon");
            return;
        }

        // Collect category data to pass to parent
        const categoryData = {
            name: name.trim(),
            value: name.trim().replace(/\s+/g, ''),
            status,
            imageFile,  // Pass the file object for later FormData creation
            icon: selectedIcon  // { pack: "fi", name: "FiUser" }

        };
        console.log(categoryData);

        message.success("Category details saved! Now add a subcategory.");
        // Pass category data to parent to move to subcategory step
        onSuccess?.(categoryData);
    };

    return (
        <div className="space-y-6 max-w-2xl">
            {/* Category Image - same UI as CreateBidForm Photos (upload tile + behaviour) */}
            <div className="mb-6 text-left">
                <h3 className="text-lg font-semibold mb-1 text-gray-900 text-left">
                    Category Image <span className="text-red-500">*</span>
                </h3>
                <p className="text-sm text-gray-500 mb-4 text-left">
                    Add one photo for this category.
                </p>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                />
                <div className="flex gap-3 overflow-x-auto pb-2">
                    {imagePreview ? (
                        <div className="relative w-28 h-28 rounded-xl overflow-hidden border shadow-sm shrink-0 transition-transform duration-300">
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={removeImage}
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black"
                                aria-label="Remove image"
                            >
                                ✕
                            </button>
                        </div>
                    ) : null}
                    <button
                        type="button"
                        onClick={handleImageClick}
                        className="w-28 h-28 shrink-0 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:border-blue-500 transition cursor-pointer bg-white"
                    >
                        <span className="text-2xl leading-none mb-1">+</span>
                        <span className="text-sm text-gray-600">Upload</span>
                    </button>
                </div>
            </div>

            <div className="my-6 border-t border-gray-200" />

            {/* Icon Picker */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Icon <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setIconPickerVisible(true)}
                        className={`
                            w-16 h-16 rounded-lg border-2 flex items-center justify-center
                            transition-all duration-200 cursor-pointer
                            ${selectedIcon
                                ? "border-[#1955EB] bg-blue-50"
                                : "border-dashed border-gray-300 hover:border-[#1955EB] hover:bg-gray-50"
                            }
                        `}
                    >
                        {selectedIcon ? (
                            <DynamicIcon icon={selectedIcon} className="text-2xl text-[#1955EB]" />
                        ) : (
                            <span className="text-gray-400 text-xs text-center">Select<br />Icon</span>
                        )}
                    </button>
                    {selectedIcon && (
                        <div className="text-sm text-gray-500">
                            <span className="font-medium">{selectedIcon.name}</span>
                            <button
                                type="button"
                                onClick={() => setSelectedIcon(null)}
                                className="ml-2 text-red-500 hover:text-red-700"
                            >
                                Remove
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Category Name - same as CreateBidForm Product Info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                        placeholder="Enter category name (e.g., Vehicles)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-10"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                    </label>
                    <Select
                        value={status}
                        onChange={(val) => setStatus(val)}
                        className="w-full"
                        style={{ height: 40 }}
                        options={[
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ]}
                    />
                </div>
            </div>

            {/* Footer Buttons - same styling as CreateBidForm primary button */}
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={submitLoading}
                >
                    Continue to Subcategory
                </Button>
            </div>

            {/* Icon Picker Modal */}
            <IconPicker
                visible={iconPickerVisible}
                onClose={() => setIconPickerVisible(false)}
                onSelect={setSelectedIcon}
                selectedIcon={selectedIcon}
            />
        </div>
    );
}

export default CategoryForm;
